import Marquee from "react-fast-marquee";
import { MapPin, Calendar, Clock, Star, Sparkles, PartyPopper, Trophy, Rocket, ExternalLink } from "lucide-react";
import { EVENT5K } from "@/constants/testIds";
import Countdown from "./Countdown";
import { goToRunSignup } from "@/lib/runsignup";

const LOGO = "/images/logo-2026.png";

export default function Hero({ onSponsor, onVendor, onVolunteer }) {
  const onRegister = () => goToRunSignup("register");
  return (
    <section
      id="top"
      data-testid={EVENT5K.sectionHero}
      className="relative min-h-screen overflow-hidden bg-[#0A0A0A] pt-24 pb-16"
    >
      <div className="absolute inset-0 grid-pattern opacity-70" />
      <div className="noise-overlay" />

      {/* Background marquee */}
      <div className="absolute inset-x-0 top-1/3 -translate-y-1/2 pointer-events-none select-none">
        <Marquee gradient={false} speed={38}>
          <span className="marquee-text text-[10rem] md:text-[16rem] px-8 leading-none">
            RUN&nbsp;TO&nbsp;EMPOWER&nbsp;&nbsp;•&nbsp;&nbsp;
          </span>
          <span className="marquee-text text-[10rem] md:text-[16rem] px-8 leading-none">
            RUN&nbsp;TO&nbsp;EMPOWER&nbsp;&nbsp;•&nbsp;&nbsp;
          </span>
        </Marquee>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-14">
        {/* Top: 5K blowup on left, logo + date banner stacked on right — heights aligned */}
        <div className="flex flex-col md:flex-row items-stretch justify-center gap-6 md:gap-8 mb-10 md:mb-14">
          <div className="relative flex-1 max-w-3xl w-full">
            <img
              src="/images/finish-line-2025.png"
              alt="Runners bursting through the yellow FINISH arch at the 2025 Run to Empower Entrepreneurs 5K"
              loading="eager"
              className="w-full h-full max-h-[480px] object-cover border border-[#27272A] drop-shadow-[0_0_40px_rgba(0,166,81,0.25)]"
            />
            <div className="absolute -bottom-3 left-4 md:left-6 bg-[#0A0A0A] px-3 py-1 border border-[#EC008C]/50">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#EC008C] font-bold">
                2025 · Finish Line
              </span>
            </div>
          </div>

          <div className="flex flex-col justify-between items-center gap-4 shrink-0 md:w-[24rem]">
            <img
              src={LOGO}
              alt="Run to Empower Entrepreneurs — Freedom In Me Business Showers"
              loading="eager"
              className="w-64 sm:w-72 md:w-80 lg:w-[22rem] h-auto drop-shadow-[0_0_30px_rgba(236,0,140,0.4)]"
            />

            {/* Animated 2026 date banner — sits at the bottom of the column, level with the picture */}
            <div className="relative inline-flex items-center gap-3 md:gap-4 bg-gradient-to-r from-[#00A651] via-[#1FBF3E] to-[#00A651] px-5 md:px-7 py-2.5 md:py-3 anim-pulse-glow border-2 border-black shadow-[0_0_40px_rgba(0,166,81,0.5)] mt-auto">
              <span className="absolute -top-2 -left-2 w-3.5 h-3.5 bg-[#EC008C] rotate-45 anim-twinkle" />
              <span className="absolute -top-2 -right-2 w-3.5 h-3.5 bg-[#EC008C] rotate-45 anim-twinkle" style={{ animationDelay: "0.3s" }} />
              <span className="absolute -bottom-2 -left-2 w-3.5 h-3.5 bg-[#EC008C] rotate-45 anim-twinkle" style={{ animationDelay: "0.6s" }} />
              <span className="absolute -bottom-2 -right-2 w-3.5 h-3.5 bg-[#EC008C] rotate-45 anim-twinkle" style={{ animationDelay: "0.9s" }} />
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-black shrink-0" />
              <div className="text-center">
                <div className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-black font-black">Save the date</div>
                <div className="font-display text-xl md:text-2xl lg:text-3xl font-black uppercase tracking-tight text-black leading-none whitespace-nowrap">
                  Sat · Nov 7, <span className="text-[#EC008C] anim-bounce-slow inline-block">2026</span> · 9 AM
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* Left: content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#EC008C]/50 bg-[#EC008C]/10 px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 bg-[#EC008C] animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.24em] text-[#EC008C] font-semibold">
                A Freedom In Me Business Showers Movement
              </span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] xl:text-[7rem] font-black uppercase leading-[0.9] tracking-tighter text-white break-words">
              Run to<br />
              <span className="text-[#00A651] text-shadow-glow-green">Empower</span>{" "}
              <span className="text-[#EC008C] text-shadow-glow-pink">Entrepreneurs</span>
              <span className="block text-2xl md:text-4xl lg:text-5xl mt-3 text-[#A3A3A3] font-bold">
                5K &middot; 1-Mile Fun Run
              </span>
            </h1>

            <div className="mt-5 md:mt-6 text-[13px] md:text-base font-bold uppercase tracking-[0.24em]">
              <span className="text-white">Run.</span>{" "}
              <span className="text-[#00A651]">Walk.</span>{" "}
              <span className="text-[#EC008C]">Bubble.</span>{" "}
              <span className="text-white">Empower.</span>
              <span className="block mt-1 text-[10px] md:text-xs tracking-[0.32em] text-[#A3A3A3] font-semibold">
                Every mile empowers an entrepreneur.
              </span>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl">
              <div className="flex items-center gap-3 border-l-2 border-[#00A651] pl-3">
                <Calendar className="w-5 h-5 text-[#00A651] shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Date</div>
                  <div className="font-display text-lg text-white font-bold uppercase">Nov 7, 2026</div>
                </div>
              </div>
              <div className="flex items-center gap-3 border-l-2 border-[#EC008C] pl-3">
                <Clock className="w-5 h-5 text-[#EC008C] shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Race Start</div>
                  <div className="font-display text-lg text-white font-bold uppercase">9:00 AM</div>
                </div>
              </div>
              <div className="flex items-center gap-3 border-l-2 border-[#00A651] pl-3">
                <MapPin className="w-5 h-5 text-[#00A651] shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Location</div>
                  <div className="font-display text-lg text-white font-bold uppercase">Concord, NC</div>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                data-testid={EVENT5K.heroRegister}
                onClick={onRegister}
                className="group bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-0.5"
              >
                Register Now &nbsp;→
              </button>
              <button
                data-testid={EVENT5K.heroSponsor}
                onClick={onSponsor}
                className="bg-transparent border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-sm px-6 py-4 transition-all"
              >
                Become a Sponsor
              </button>
              <button
                data-testid={EVENT5K.heroVendor}
                onClick={onVendor}
                className="bg-transparent border border-white/40 text-white hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-sm px-6 py-4 transition-all"
              >
                Become a Vendor
              </button>
              <button
                data-testid={EVENT5K.heroVolunteer}
                onClick={onVolunteer}
                className="bg-transparent border border-white/40 text-white hover:border-[#EC008C] hover:text-[#EC008C] font-bold uppercase tracking-widest text-sm px-6 py-4 transition-all"
              >
                Volunteer
              </button>
            </div>
          </div>

          {/* Right: hero image + countdown */}
          <div className="lg:col-span-5">
            <div className="relative bg-[#141414] border border-[#27272A] p-6 md:p-8">
              <div className="absolute -top-3 left-6 bg-[#0A0A0A] px-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#EC008C] font-bold">
                  Race Day · 2025
                </span>
              </div>

              {/* Animated fun decorations around the kids photo */}
              <div className="pointer-events-none absolute -top-4 -right-4 z-20 anim-float">
                <div className="relative flex items-center justify-center">
                  <span className="absolute inline-flex h-full w-full rounded-full border-2 border-[#EC008C] anim-ring" />
                  <div className="relative bg-[#EC008C] text-white font-display font-black uppercase text-[10px] md:text-xs tracking-widest px-3 py-2 rotate-[-8deg] shadow-lg">
                    Fun Run!
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute top-16 -left-4 z-20 anim-float-slower">
                <div className="bg-[#00A651] text-black font-display font-black uppercase text-[10px] md:text-xs tracking-widest px-3 py-2 rotate-[6deg] shadow-lg">
                  Kids Welcome
                </div>
              </div>
              <Sparkles className="pointer-events-none absolute top-8 right-10 z-20 w-5 h-5 md:w-6 md:h-6 text-[#00A651] anim-twinkle" />
              <Star className="pointer-events-none absolute bottom-12 -left-2 z-20 w-4 h-4 md:w-5 md:h-5 text-[#EC008C] anim-twinkle" style={{ animationDelay: "0.4s" }} />
              <Star className="pointer-events-none absolute top-24 -right-2 z-20 w-3 h-3 md:w-4 md:h-4 text-white anim-twinkle" style={{ animationDelay: "0.8s" }} />
              <PartyPopper className="pointer-events-none absolute -bottom-3 right-6 z-20 w-6 h-6 md:w-7 md:h-7 text-[#EC008C] anim-wiggle" />

              <div className="aspect-square bg-black overflow-hidden relative">
                <img
                  src="/images/youth-runner-2.jpg"
                  alt="Youth runners on the course at the 2025 Run to Empower Entrepreneurs 5K"
                  loading="eager"
                  className="w-full h-full object-cover"
                />

                {/* Confetti dots floating along the top edge */}
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-10 z-10">
                  <span className="absolute top-2 left-[8%] w-2 h-2 bg-[#EC008C] rotate-12 anim-float" />
                  <span className="absolute top-4 left-[22%] w-1.5 h-3 bg-[#00A651] anim-bounce-slow" style={{ animationDelay: "0.3s" }} />
                  <span className="absolute top-1 left-[38%] w-2 h-2 rounded-full bg-white anim-twinkle" />
                  <span className="absolute top-5 left-[58%] w-2.5 h-2.5 bg-[#EC008C] rotate-45 anim-float-slower" style={{ animationDelay: "0.5s" }} />
                  <span className="absolute top-2 left-[74%] w-2 h-2 rounded-full bg-[#00A651] anim-twinkle" style={{ animationDelay: "0.7s" }} />
                  <span className="absolute top-6 left-[90%] w-1.5 h-3 bg-white anim-bounce-slow" style={{ animationDelay: "0.2s" }} />
                </div>

                {/* GO GO GO animated banner (near her feet) */}
                <div className="pointer-events-none absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-10 anim-wiggle">
                  <div className="bg-[#EC008C]/95 backdrop-blur-sm border border-white/20 px-3 py-1 shadow-[0_0_20px_rgba(236,0,140,0.6)]">
                    <span className="font-display text-sm md:text-base font-black uppercase tracking-widest text-white">
                      Go · Go · Go!
                    </span>
                  </div>
                </div>

                {/* Spinning race medal in a safe corner (bottom-right) */}
                <div className="pointer-events-none absolute bottom-16 right-3 z-10">
                  <div className="relative">
                    <span className="absolute inset-0 rounded-full border-2 border-[#00A651] anim-ring" />
                    <div
                      className="relative w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#00A651] to-[#1FBF3E] border-2 border-white flex items-center justify-center shadow-lg"
                      style={{ animation: "wiggle 3s ease-in-out infinite" }}
                    >
                      <span className="font-display text-lg md:text-xl font-black text-black leading-none">5K</span>
                    </div>
                  </div>
                </div>

                {/* Confetti dots floating along the LEFT edge (avoids faces which are center) */}
                <div className="pointer-events-none absolute top-0 left-0 bottom-0 w-8 z-10">
                  <span className="absolute top-[30%] left-2 w-2 h-2 bg-[#00A651] rotate-45 anim-float-slower" />
                  <span className="absolute top-[50%] left-1 w-1.5 h-1.5 rounded-full bg-white anim-twinkle" style={{ animationDelay: "0.5s" }} />
                  <span className="absolute top-[70%] left-3 w-2 h-2 bg-[#EC008C] rotate-12 anim-float" style={{ animationDelay: "0.2s" }} />
                </div>

                <div className="absolute top-3 left-3 z-10 bg-black/70 backdrop-blur-sm border border-[#00A651]/50 px-2.5 py-1 anim-bounce-slow">
                  <div className="flex items-center gap-1.5">
                    <Rocket className="w-3.5 h-3.5 text-[#00A651]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white font-bold">1-Mile Fun Run</span>
                  </div>
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 z-10">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[#EC008C] anim-wiggle" />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">
                      Bibs #232 · #242 · #239
                    </div>
                  </div>
                  <div className="mt-1 font-display text-lg font-bold uppercase text-white leading-tight">
                    Next up: your finish line.
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-dashed border-[#27272A]">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] mb-2">
                  Race day countdown
                </div>
                <Countdown />
              </div>
            </div>
            <div className="mt-4 text-center text-xs text-[#71717A] uppercase tracking-widest">
              Hartsell Shelter · Frank Liske Park
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
